import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { Upload, Building, Loader2, ImageIcon, X, CalendarDays, Flag, Clock, Plus, Trash2, HelpCircle, CheckCircle, Palette, Mail, Key, Copy, Users, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const GIORNI_SETTIMANA = [
  { value: 0, label: "Domenica" },
  { value: 1, label: "Lunedì" },
  { value: 2, label: "Martedì" },
  { value: 3, label: "Mercoledì" },
  { value: 4, label: "Giovedì" },
  { value: 5, label: "Venerdì" },
  { value: 6, label: "Sabato" },
];

const MESI = [
  { value: 1, label: "Gennaio" },
  { value: 2, label: "Febbraio" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Aprile" },
  { value: 5, label: "Maggio" },
  { value: 6, label: "Giugno" },
  { value: 7, label: "Luglio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Settembre" },
  { value: 10, label: "Ottobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Dicembre" },
];

export default function SettingsPage() {
  const { user, token, settings, refreshSettings, completeOnboarding } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOnboarding = searchParams.get("onboarding") === "true";
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(isOnboarding);
  
  // Onboarding Wizard State
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [activeTab, setActiveTab] = useState(user?.ruolo === "dipendente" ? "notifications" : "general");
  
  // Handle tab parameter from URL
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  // Check if this is the first time titolare visits settings
  useEffect(() => {
    if (user?.ruolo === "titolare") {
      const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
      if (!hasCompletedOnboarding && isOnboarding) {
        setShowOnboardingWizard(true);
        setOnboardingStep(1);
      }
    }
  }, [user, isOnboarding]);
  
  // Onboarding steps configuration
  const ONBOARDING_STEPS = [
    { step: 0, tab: null, title: "", content: "" },
    { 
      step: 1, 
      tab: "general", 
      title: "🎉 Benvenuto! Configura la tua azienda",
      content: (
        <div className="space-y-2">
          <p><strong>1.</strong> Carica il logo della tua azienda</p>
          <p><strong>2.</strong> Segnati il <strong>Codice Invito Dipendenti</strong> da inoltrare ai tuoi dipendenti per registrarsi</p>
          <p><strong>3.</strong> Vai in fondo pagina e <strong>salva le modifiche</strong></p>
          <p className="mt-3 flex items-center gap-2">
            <strong>4.</strong> Poi spostati su 
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 mx-1 animate-pulse border-2 border-purple-400"
              onClick={() => { setActiveTab("theme"); setOnboardingStep(2); }}
            >
              <Palette className="h-4 w-4" />
              Aspetto
            </Button>
          </p>
        </div>
      )
    },
    { 
      step: 2, 
      tab: "theme", 
      title: "🎨 Scegli e applica il tema della tua Azienda",
      content: (
        <div className="space-y-2">
          <p>Seleziona il colore che rappresenta meglio la tua azienda, poi clicca <strong>"Applica tema"</strong>.</p>
          <p className="mt-3 flex items-center gap-2">
            Poi spostati su 
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 mx-1 animate-pulse border-2 border-purple-400"
              onClick={() => { setActiveTab("workweek"); setOnboardingStep(3); }}
            >
              <CalendarDays className="h-4 w-4" />
              Settimana
            </Button>
          </p>
        </div>
      )
    },
    { 
      step: 3, 
      tab: "workweek", 
      title: "📅 Configura la settimana lavorativa",
      content: (
        <div className="space-y-2">
          <p>Imposta i giorni lavorativi e l'eventuale giorno di chiusura infrasettimanale.</p>
          <p>Una volta fatto, clicca il pulsante <strong className="text-emerald-600">"Salva settimana lavorativa"</strong> (lampeggiante).</p>
          <p className="mt-3 flex items-center gap-2">
            Poi spostati su 
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 mx-1 animate-pulse border-2 border-purple-400"
              onClick={() => { setActiveTab("limits"); setOnboardingStep(4); }}
            >
              <Clock className="h-4 w-4" />
              Limiti
            </Button>
            per configurare fino a quando i dipendenti possono richiedere ferie e permessi. Ricordati di salvare.
          </p>
        </div>
      )
    },
    { 
      step: 4, 
      tab: "limits", 
      title: "⏰ Configura i limiti delle richieste",
      content: (
        <div className="space-y-2">
          <p>Imposta fino a quando i dipendenti possono richiedere ferie e permessi.</p>
          <p>Clicca <strong>"Salva limiti"</strong> dopo aver configurato.</p>
          <p className="mt-3 flex items-center gap-2">
            Poi spostati su 
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 mx-1 animate-pulse border-2 border-purple-400"
              onClick={() => { setActiveTab("holidays"); setOnboardingStep(5); }}
            >
              <Flag className="h-4 w-4" />
              Festività
            </Button>
            se vuoi inserire le festività locali. Ricordati di salvare.
          </p>
        </div>
      )
    },
    { 
      step: 5, 
      tab: "holidays", 
      title: "🎌 Aggiungi festività locali",
      content: (
        <div className="space-y-2">
          <p>Aggiungi le festività locali della tua zona (es. Santo Patrono).</p>
          <p className="mt-3 flex items-center gap-2">
            Poi spostati su 
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-1 mx-1 animate-pulse border-2 border-purple-400"
              onClick={() => { setActiveTab("notifications"); setOnboardingStep(6); }}
            >
              <Mail className="h-4 w-4" />
              Email
            </Button>
            e scegli quali notifiche vuoi ricevere via email oltre alla campanella.
          </p>
        </div>
      )
    },
    { 
      step: 6, 
      tab: "notifications", 
      title: "📧 Configura le notifiche email",
      content: (
        <div className="space-y-2">
          <p>Scegli quali notifiche vuoi ricevere via email oltre alla campanella in-app.</p>
          <p>Clicca <strong>"Salva preferenze email"</strong> dopo aver scelto.</p>
          <p className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <strong>✅ Configurazione completata!</strong><br/>
            Adesso aspetta che si registrino dipendenti e/o amministrativi.<br/>
            <span className="text-sm text-slate-600">Gli amministrativi puoi utilizzarli per dare mansioni affinché eseguano alcuni compiti che gli assegnerai.</span>
          </p>
          <Button 
            className="mt-3 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => {
              localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
              setShowOnboardingWizard(false);
              setOnboardingStep(0);
              toast.success("Configurazione completata! Benvenuto in Ferie e Permessi!");
              navigate("/dashboard");
            }}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Completa configurazione
          </Button>
        </div>
      )
    },
  ];
  
  const currentOnboardingStep = ONBOARDING_STEPS.find(s => s.step === onboardingStep);
  
  const [companyName, setCompanyName] = useState(settings.nome_azienda);
  const [logoPreview, setLogoPreview] = useState(settings.logo_base64);
  const [temaColore, setTemaColore] = useState(settings.tema_colore ?? "blue");
  const [giornoInizio, setGiornoInizio] = useState(settings.giorno_inizio_settimana ?? 1);
  const [giornoFine, setGiornoFine] = useState(settings.giorno_fine_settimana ?? 5);
  const [limiteRichieste, setLimiteRichieste] = useState(settings.limite_richieste ?? "tutto_anno");
  const [limiteMesi, setLimiteMesi] = useState(settings.limite_mesi ?? 3);
  const [chiusuraAttiva, setChiusuraAttiva] = useState(settings.chiusura_infrasettimanale_attiva ?? false);
  const [chiusuraGiorno, setChiusuraGiorno] = useState(settings.chiusura_infrasettimanale_giorno ?? 3);
  const [chiusuraDalle, setChiusuraDalle] = useState(settings.chiusura_infrasettimanale_dalle ?? "12:00");
  const [chiusuraAlle, setChiusuraAlle] = useState(settings.chiusura_infrasettimanale_alle ?? "15:00");
  const [mostraSaldiDipendenti, setMostraSaldiDipendenti] = useState(settings.mostra_saldi_dipendenti ?? false);
  const [mostraProspettoFerie, setMostraProspettoFerie] = useState(settings.mostra_prospetto_ferie ?? true);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingWorkweek, setSavingWorkweek] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [localHolidays, setLocalHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ nome: "", giorno: 1, mese: 1 });
  const [addingHoliday, setAddingHoliday] = useState(false);
  const [deleteHolidayId, setDeleteHolidayId] = useState(null);
  const [emailPreferences, setEmailPreferences] = useState({
    nuovo_dipendente: false,
    nuova_richiesta: false,
    richiesta_approvata: false,
    richiesta_rifiutata: false,
    evento_assegnato: false,
    nuovo_evento_calendario: false
  });
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [loadingInviteCode, setLoadingInviteCode] = useState(false);
  const [regeneratingCode, setRegeneratingCode] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [notificationEmailVerified, setNotificationEmailVerified] = useState(true);
  const [changingEmail, setChangingEmail] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState(false);
  const fileInputRef = useRef(null);

  // Theme color options
  const THEME_COLORS = [
    // Blu
    { value: "blue", label: "Blu", bg: "bg-blue-500", preview: "#3b82f6" },
    { value: "sky", label: "Celeste", bg: "bg-sky-500", preview: "#0ea5e9" },
    { value: "indigo", label: "Indaco", bg: "bg-indigo-500", preview: "#6366f1" },
    { value: "cyan", label: "Ciano", bg: "bg-cyan-500", preview: "#06b6d4" },
    // Verde
    { value: "green", label: "Verde", bg: "bg-green-500", preview: "#22c55e" },
    { value: "emerald", label: "Smeraldo", bg: "bg-emerald-500", preview: "#10b981" },
    { value: "teal", label: "Turchese", bg: "bg-teal-500", preview: "#14b8a6" },
    { value: "lime", label: "Lime", bg: "bg-lime-500", preview: "#84cc16" },
    // Viola/Rosa
    { value: "violet", label: "Viola", bg: "bg-violet-500", preview: "#8b5cf6" },
    { value: "purple", label: "Porpora", bg: "bg-purple-500", preview: "#a855f7" },
    { value: "fuchsia", label: "Fucsia", bg: "bg-fuchsia-500", preview: "#d946ef" },
    { value: "pink", label: "Rosa Chiaro", bg: "bg-pink-500", preview: "#ec4899" },
    { value: "rose", label: "Rosa", bg: "bg-rose-500", preview: "#f43f5e" },
    // Caldi
    { value: "red", label: "Rosso", bg: "bg-red-500", preview: "#ef4444" },
    { value: "orange", label: "Arancione", bg: "bg-orange-500", preview: "#f97316" },
    { value: "amber", label: "Ambra", bg: "bg-amber-500", preview: "#f59e0b" },
    { value: "yellow", label: "Giallo", bg: "bg-yellow-500", preview: "#eab308" },
    // Neutri
    { value: "slate", label: "Ardesia", bg: "bg-slate-500", preview: "#64748b" },
    { value: "gray", label: "Grigio", bg: "bg-gray-500", preview: "#6b7280" },
    { value: "zinc", label: "Zinco", bg: "bg-zinc-500", preview: "#71717a" },
    { value: "stone", label: "Pietra", bg: "bg-stone-500", preview: "#78716c" },
  ];

  // Helper functions to check permissions
  const isTitolare = user?.ruolo === "titolare";
  const isAmministrativo = user?.ruolo === "amministrativo";
  const permessi = user?.permessi_admin || {};
  
  const canEditInfoAzienda = isTitolare || (isAmministrativo && permessi.gestione_info_azienda);
  const canEditGiorniLavorativi = isTitolare || (isAmministrativo && permessi.gestione_giorni_lavorativi);
  const canEditLimitiRichieste = isTitolare || (isAmministrativo && permessi.gestione_limiti_richieste);

  useEffect(() => {
    if (user?.ruolo === "titolare" || user?.ruolo === "amministrativo") {
      fetchLocalHolidays();
    }
    if (user?.ruolo === "titolare") {
      fetchInviteCode();
    }
    fetchEmailPreferences();
    
    // Handle Stripe payment return
    handleStripeReturn();
    
    // Scroll to subscription section if hash is present
    if (window.location.hash === "#subscription") {
      setTimeout(() => {
        const element = document.getElementById("subscription");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 500);
    }
  }, [token, user]);

  const handleStripeReturn = async () => {
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (payment === "success" && sessionId) {
      // Poll payment status
      toast.loading("Verifica pagamento in corso...", { id: "payment-check" });
      
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;
      
      const checkStatus = async () => {
        try {
          const response = await axios.get(
            `${API}/stripe/checkout-status/${sessionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.payment_status === "paid") {
            toast.success(`Upgrade al piano ${response.data.plan_name} completato!`, { id: "payment-check" });
            // Clear URL params
            navigate("/settings", { replace: true });
            // Refresh page to update subscription status
            window.location.reload();
            return;
          } else if (response.data.status === "expired") {
            toast.error("Sessione di pagamento scaduta. Riprova.", { id: "payment-check" });
            navigate("/settings", { replace: true });
            return;
          }
          
          // Keep polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, pollInterval);
          } else {
            toast.error("Verifica pagamento scaduta. Controlla la tua email.", { id: "payment-check" });
            navigate("/settings", { replace: true });
          }
        } catch (error) {
          console.error("Error checking payment status:", error);
          toast.error("Errore verifica pagamento.", { id: "payment-check" });
          navigate("/settings", { replace: true });
        }
      };
      
      checkStatus();
    } else if (payment === "cancelled") {
      toast.info("Pagamento annullato.");
      navigate("/settings", { replace: true });
    }
  };

  const fetchInviteCode = async () => {
    setLoadingInviteCode(true);
    try {
      const response = await axios.get(`${API}/company/invite-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteCode(response.data.codice_invito);
    } catch (error) {
      console.error("Failed to fetch invite code");
    } finally {
      setLoadingInviteCode(false);
    }
  };

  const handleRegenerateCode = async () => {
    setRegeneratingCode(true);
    try {
      const response = await axios.post(`${API}/company/regenerate-invite-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteCode(response.data.codice_invito);
      toast.success("Nuovo codice generato!");
    } catch (error) {
      toast.error("Errore nella generazione del codice");
    } finally {
      setRegeneratingCode(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    toast.success("Codice copiato negli appunti!");
  };

  const fetchEmailPreferences = async () => {
    try {
      const response = await axios.get(`${API}/auth/email-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { notification_email, notification_email_verified, registration_email, ...prefs } = response.data;
      setEmailPreferences(prefs);
      setNotificationEmail(notification_email || registration_email);
      setRegistrationEmail(registration_email);
      setNotificationEmailVerified(notification_email_verified !== false);
    } catch (error) {
      console.error("Failed to fetch email preferences");
    }
  };

  const handleChangeNotificationEmail = async () => {
    if (!notificationEmail.trim()) {
      toast.error("Inserisci un indirizzo email valido");
      return;
    }
    
    setChangingEmail(true);
    try {
      const response = await axios.post(`${API}/auth/change-notification-email`, 
        { email: notificationEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.verified) {
        setNotificationEmailVerified(true);
        setPendingEmailChange(false);
        toast.success(response.data.message);
      } else {
        setPendingEmailChange(true);
        setNotificationEmailVerified(false);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio email");
    } finally {
      setChangingEmail(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      toast.error("Inserisci il codice a 6 cifre");
      return;
    }
    
    setVerifyingCode(true);
    try {
      const response = await axios.post(`${API}/auth/verify-notification-email`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationEmailVerified(true);
      setPendingEmailChange(false);
      setVerificationCode("");
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella verifica");
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSaveEmailPreferences = async () => {
    setSavingEmailPrefs(true);
    try {
      await axios.put(`${API}/auth/email-preferences`, emailPreferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Preferenze email salvate!");
    } catch (error) {
      toast.error("Errore nel salvataggio delle preferenze");
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const fetchLocalHolidays = async () => {
    try {
      const response = await axios.get(`${API}/local-holidays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocalHolidays(response.data);
    } catch (error) {
      console.error("Failed to fetch local holidays");
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Il file è troppo grande. Massimo 2MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      await axios.put(
        `${API}/settings`,
        {
          nome_azienda: companyName,
          logo_base64: logoPreview,
          mostra_saldi_dipendenti: mostraSaldiDipendenti,
          mostra_prospetto_ferie: mostraProspettoFerie
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Impostazioni salvate con successo!");
      setTimeout(() => {
        refreshSettings();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSaveWorkweek = async () => {
    if (giornoInizio > giornoFine && giornoFine !== 0) {
      toast.error("Il giorno di inizio non può essere dopo il giorno di fine");
      return;
    }
    
    setSavingWorkweek(true);
    try {
      await axios.put(
        `${API}/settings`,
        {
          giorno_inizio_settimana: giornoInizio,
          giorno_fine_settimana: giornoFine,
          chiusura_infrasettimanale_attiva: chiusuraAttiva,
          chiusura_infrasettimanale_giorno: chiusuraGiorno,
          chiusura_infrasettimanale_dalle: chiusuraDalle,
          chiusura_infrasettimanale_alle: chiusuraAlle
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settimana lavorativa salvata con successo!");
      setTimeout(() => {
        refreshSettings();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingWorkweek(false);
    }
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await axios.put(
        `${API}/settings`,
        {
          limite_richieste: limiteRichieste,
          limite_mesi: limiteMesi
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Limiti richieste salvati con successo!");
      setTimeout(() => {
        refreshSettings();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSaveTheme = async () => {
    setSavingTheme(true);
    try {
      await axios.put(
        `${API}/settings`,
        { tema_colore: temaColore },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Tema colore salvato con successo!");
      setTimeout(() => {
        refreshSettings();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingTheme(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.nome.trim()) {
      toast.error("Inserisci il nome della festività");
      return;
    }
    setAddingHoliday(true);
    try {
      await axios.post(`${API}/local-holidays`, newHoliday, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Festività aggiunta!");
      setNewHoliday({ nome: "", giorno: 1, mese: 1 });
      fetchLocalHolidays();
    } catch (error) {
      toast.error("Errore nell'aggiunta della festività");
    } finally {
      setAddingHoliday(false);
    }
  };

  const handleConfirmDeleteHoliday = async () => {
    try {
      await axios.delete(`${API}/local-holidays/${deleteHolidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Festività eliminata!");
      setDeleteHolidayId(null);
      fetchLocalHolidays();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const getSettimanaLabel = () => {
    const inizio = GIORNI_SETTIMANA.find(g => g.value === giornoInizio)?.label || "Lunedì";
    const fine = GIORNI_SETTIMANA.find(g => g.value === giornoFine)?.label || "Venerdì";
    return `${inizio} - ${fine}`;
  };

  const getLimiteLabel = () => {
    switch (limiteRichieste) {
      case "questo_mese": return "Solo questo mese";
      case "entro_x_mesi": return `Entro ${limiteMesi} mesi`;
      case "tutto_anno": return "Tutto l'anno";
      default: return "Tutto l'anno";
    }
  };

  const handleCompleteOnboarding = async () => {
    await completeOnboarding();
    setShowOnboardingBanner(false);
    toast.success("Benvenuto! La configurazione iniziale è completata.");
    navigate("/dashboard");
  };

  // Help tooltip component for onboarding - works on hover and click/touch
  const HelpTooltip = ({ content }) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button 
            type="button" 
            className="inline-flex items-center justify-center ml-2 h-5 w-5 rounded-full bg-theme-100 hover:bg-theme-200 text-theme-600 transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent 
          side="bottom" 
          align="start"
          className="max-w-[280px] sm:max-w-xs p-3 text-sm bg-slate-900 text-white border-slate-800 z-50"
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-3xl">
        {/* Onboarding Wizard Banner - Step by Step */}
        {showOnboardingWizard && user?.ruolo === "titolare" && currentOnboardingStep && currentOnboardingStep.step > 0 && (
          <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 shadow-lg" data-testid="onboarding-wizard">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-xl shrink-0">
                  <HelpCircle className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="font-bold text-lg text-purple-900">{currentOnboardingStep.title}</h3>
                    <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full shrink-0">
                      Passo {onboardingStep}/6
                    </span>
                  </div>
                  <div className="text-sm text-slate-700">
                    {currentOnboardingStep.content}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-purple-200 flex items-center justify-between">
                <button 
                  className="text-sm text-purple-600 hover:text-purple-800 underline"
                  onClick={() => {
                    localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
                    setShowOnboardingWizard(false);
                  }}
                >
                  Salta tutorial
                </button>
                <div className="flex gap-1">
                  {[1,2,3,4,5,6].map(step => (
                    <div 
                      key={step} 
                      className={`w-2 h-2 rounded-full transition-colors ${
                        step === onboardingStep ? 'bg-purple-600' : 
                        step < onboardingStep ? 'bg-purple-300' : 'bg-purple-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Original Onboarding Banner - Keep for backwards compatibility */}
        {showOnboardingBanner && !showOnboardingWizard && user?.ruolo === "titolare" && (
          <Card className="card-theme-highlight" data-testid="onboarding-banner">
            <CardContent className="p-3 sm:py-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-theme-100 rounded-lg shrink-0">
                    <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-theme-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Benvenuto! Configura la tua azienda</h3>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      Tocca l'icona <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-theme-100 text-theme-600 mx-0.5"><HelpCircle className="h-2.5 w-2.5" /></span> accanto a ogni sezione per vedere le istruzioni.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleCompleteOnboarding}
                  className="w-full sm:w-auto btn-theme gap-2 text-sm"
                  data-testid="complete-onboarding-btn"
                >
                  <CheckCircle className="h-4 w-4" />
                  Ho capito
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" data-testid="settings-title">
            Impostazioni
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Gestisci le impostazioni dell'applicazione</p>
        </div>

        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          defaultValue={user?.ruolo === "dipendente" ? "notifications" : "general"} 
          className="space-y-6"
        >
          <TabsList className="tabs-theme-list flex-wrap h-auto gap-1 p-1 w-full justify-start">
            <TabsTrigger 
              value="general" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${user?.ruolo === "dipendente" ? "hidden" : ""} ${showOnboardingWizard && onboardingStep === 1 ? "ring-2 ring-purple-400 ring-offset-2" : ""}`} 
              data-testid="tab-general"
            >
              <Building className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Generale</span>
            </TabsTrigger>
            <TabsTrigger 
              value="theme" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${user?.ruolo === "titolare" ? "" : "hidden"} ${showOnboardingWizard && onboardingStep === 2 ? "ring-2 ring-purple-400 ring-offset-2" : ""}`} 
              data-testid="tab-theme"
            >
              <Palette className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Aspetto</span>
            </TabsTrigger>
            <TabsTrigger 
              value="workweek" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${user?.ruolo === "dipendente" ? "hidden" : ""} ${showOnboardingWizard && onboardingStep === 3 ? "ring-2 ring-purple-400 ring-offset-2" : ""}`} 
              data-testid="tab-workweek"
            >
              <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Settimana</span>
            </TabsTrigger>
            <TabsTrigger 
              value="limits" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${(user?.ruolo === "titolare" || user?.ruolo === "amministrativo") ? "" : "hidden"} ${showOnboardingWizard && onboardingStep === 4 ? "ring-2 ring-purple-400 ring-offset-2" : ""}`} 
              data-testid="tab-limits"
            >
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              Limiti
            </TabsTrigger>
            <TabsTrigger 
              value="holidays" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${(user?.ruolo === "titolare" || user?.ruolo === "amministrativo") ? "" : "hidden"} ${showOnboardingWizard && onboardingStep === 5 ? "ring-2 ring-purple-400 ring-offset-2" : ""}`} 
              data-testid="tab-holidays"
            >
              <Flag className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Festività</span>
            </TabsTrigger>
            <TabsTrigger 
              value="notifications" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${showOnboardingWizard && onboardingStep === 6 ? "ring-2 ring-purple-400 ring-offset-2" : ""}`}
              data-testid="tab-notifications"
            >
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Email</span>
            </TabsTrigger>
            <TabsTrigger 
              value="visibility" 
              className={`tabs-theme-trigger gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 ${user?.ruolo === "titolare" ? "" : "hidden"}`}
              data-testid="tab-visibility"
            >
              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Visibilità</span>
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  Informazioni Azienda
                  <HelpTooltip content="Qui puoi personalizzare il nome e il logo della tua azienda. Queste informazioni verranno mostrate nella barra laterale e nelle pagine di login." />
                </CardTitle>
                <CardDescription>
                  {canEditInfoAzienda
                    ? "Personalizza le informazioni visualizzate nell'app"
                    : "Non hai i permessi per modificare queste impostazioni"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Nome Azienda</Label>
                  <Input
                    id="company-name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="La Tua Azienda"
                    disabled={!canEditInfoAzienda}
                    data-testid="company-name-input"
                    className="max-w-md"
                  />
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label>Logo Aziendale</Label>
                  <div className="flex items-start gap-6">
                    <div className="relative">
                      {logoPreview ? (
                        <div className="relative">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="h-24 w-24 object-contain rounded-lg border border-slate-200 bg-white p-2"
                            data-testid="logo-preview"
                          />
                          <button
                            onClick={handleRemoveLogo}
                            className={`absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors ${canEditInfoAzienda ? "" : "hidden"}`}
                            data-testid="remove-logo-btn"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="h-24 w-24 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50">
                          <ImageIcon className="h-8 w-8 text-slate-300" />
                        </div>
                      )}
                    </div>

                    <div className={`space-y-2 ${canEditInfoAzienda ? "" : "hidden"}`}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                        data-testid="logo-upload-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="gap-2"
                        data-testid="upload-logo-btn"
                      >
                        <Upload className="h-4 w-4" />
                        Carica Logo
                      </Button>
                      <p className="text-xs text-slate-500">PNG, JPG o SVG. Max 2MB.</p>
                    </div>
                  </div>
                </div>

                {/* Invite Code Section - Only for Titolare */}
                {isTitolare && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Key className="h-5 w-5 text-theme-600" />
                        <Label className="text-base font-semibold">Codice Invito Dipendenti</Label>
                        <HelpTooltip content="Condividi questo codice con i tuoi dipendenti per permettere loro di registrarsi alla tua azienda." />
                      </div>
                      <p className="text-sm text-slate-500">
                        I nuovi dipendenti dovranno inserire questo codice durante la registrazione per unirsi alla tua azienda.
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-xs">
                          {loadingInviteCode ? (
                            <div className="h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                            </div>
                          ) : (
                            <div className="h-12 bg-slate-100 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300">
                              <span className="font-mono text-2xl font-bold tracking-[0.3em] text-slate-700" data-testid="invite-code-display">
                                {inviteCode}
                              </span>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          onClick={copyInviteCode}
                          disabled={loadingInviteCode || !inviteCode}
                          className="gap-2"
                          data-testid="copy-invite-code-btn"
                        >
                          <Copy className="h-4 w-4" />
                          Copia
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Condividi questo codice con i nuovi dipendenti per permettere loro di registrarsi.
                      </p>
                      
                      {/* Multi-branch section */}
                      <div className="mt-4 p-4 bg-theme-50 rounded-lg border border-theme-200">
                        <p className="text-sm font-medium text-slate-700">Vuoi gestire più filiali?</p>
                        <p className="text-xs text-slate-500 mt-1 mb-3">
                          Se hai bisogno di codici invito separati per diverse sedi, contattaci per un preventivo personalizzato.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = 'mailto:support@ferie-e-permessi.it?subject=Richiesta gestione multi-filiale'}
                          className="gap-2"
                        >
                          <Mail className="h-4 w-4" />
                          Richiedi preventivo
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                <div className={canEditInfoAzienda ? "" : "hidden"}>
                  <Separator className="my-4" />
                  <Button
                    onClick={handleSaveGeneral}
                    disabled={savingGeneral}
                    className="btn-theme btn-press"
                    data-testid="save-general-btn"
                  >
                    {savingGeneral ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</>
                    ) : (
                      "Salva Modifiche"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Theme Settings - Titolare only */}
          <TabsContent value="theme" className={`space-y-6 ${user?.ruolo === "titolare" ? "" : "hidden"}`}>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Palette className="h-5 w-5 mr-2" style={{ color: 'var(--theme-500)' }} />
                  Tema Colore
                  <HelpTooltip content="Scegli il colore principale dell'applicazione. Questa impostazione sarà applicata a tutti gli utenti della tua azienda." />
                </CardTitle>
                <CardDescription>
                  Personalizza l'aspetto dell'applicazione scegliendo un tema colore
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-base">Seleziona un colore</Label>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                    {THEME_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setTemaColore(color.value)}
                        className={`group relative flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-200 ${
                          temaColore === color.value 
                            ? "bg-slate-100 ring-2 ring-slate-900 ring-offset-2" 
                            : "hover:bg-slate-50"
                        }`}
                        data-testid={`theme-color-${color.value}`}
                      >
                        <div 
                          className={`h-10 w-10 rounded-full shadow-md transition-transform ${
                            temaColore === color.value ? "scale-110" : "group-hover:scale-105"
                          }`}
                          style={{ backgroundColor: color.preview }}
                        />
                        <span className={`text-xs font-medium ${
                          temaColore === color.value ? "text-slate-900" : "text-slate-500"
                        }`}>
                          {color.label}
                        </span>
                        {temaColore === color.value && (
                          <CheckCircle className="absolute -top-1 -right-1 h-5 w-5 text-slate-900 bg-white rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <p className="text-sm font-medium text-slate-700 mb-3">Anteprima</p>
                  <div className="flex items-center gap-4">
                    <div 
                      className="h-12 w-12 rounded-xl flex items-center justify-center text-white shadow-md"
                      style={{ backgroundColor: THEME_COLORS.find(c => c.value === temaColore)?.preview }}
                    >
                      <Building className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{settings.nome_azienda}</p>
                      <p className="text-sm" style={{ color: THEME_COLORS.find(c => c.value === temaColore)?.preview }}>
                        Tema {THEME_COLORS.find(c => c.value === temaColore)?.label}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />
                <Button
                  onClick={handleSaveTheme}
                  disabled={savingTheme}
                  className="btn-press"
                  style={{ backgroundColor: THEME_COLORS.find(c => c.value === temaColore)?.preview }}
                  data-testid="save-theme-btn"
                >
                  {savingTheme ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</>
                  ) : (
                    "Applica Tema"
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Work Week Settings */}
          <TabsContent value="workweek" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  Settimana Lavorativa
                  <HelpTooltip content="Imposta i giorni lavorativi della tua attività. Puoi anche configurare un giorno di chiusura infrasettimanale (es. riposo del mercoledì pomeriggio)." />
                </CardTitle>
                <CardDescription>
                  {canEditGiorniLavorativi
                    ? "Configura i giorni lavorativi della tua attività"
                    : "Non hai i permessi per modificare queste impostazioni"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Settimana attuale:</span> {getSettimanaLabel()}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Giorno di inizio</Label>
                    <Select
                      value={String(giornoInizio)}
                      onValueChange={(v) => setGiornoInizio(parseInt(v))}
                      disabled={!canEditGiorniLavorativi}
                    >
                      <SelectTrigger data-testid="giorno-inizio-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GIORNI_SETTIMANA.map((giorno) => (
                          <SelectItem key={giorno.value} value={String(giorno.value)}>
                            {giorno.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Giorno di fine</Label>
                    <Select
                      value={String(giornoFine)}
                      onValueChange={(v) => setGiornoFine(parseInt(v))}
                      disabled={!canEditGiorniLavorativi}
                    >
                      <SelectTrigger data-testid="giorno-fine-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GIORNI_SETTIMANA.map((giorno) => (
                          <SelectItem key={giorno.value} value={String(giorno.value)}>
                            {giorno.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Chiusura Infrasettimanale */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Giorno di chiusura infrasettimanale</Label>
                      <p className="text-sm text-slate-500">Imposta un giorno di chiusura parziale durante la settimana</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={chiusuraAttiva}
                        onChange={(e) => setChiusuraAttiva(e.target.checked)}
                        disabled={!canEditGiorniLavorativi}
                        className="sr-only peer"
                        data-testid="chiusura-attiva-toggle"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
                    </label>
                  </div>

                  <div className={`space-y-4 ${!chiusuraAttiva ? "opacity-50 pointer-events-none" : ""}`}>
                    <div className="space-y-2">
                      <Label>Giorno della settimana</Label>
                      <Select
                        value={String(chiusuraGiorno)}
                        onValueChange={(v) => setChiusuraGiorno(parseInt(v))}
                        disabled={!canEditGiorniLavorativi || !chiusuraAttiva}
                      >
                        <SelectTrigger data-testid="chiusura-giorno-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GIORNI_SETTIMANA.map((giorno) => (
                            <SelectItem key={giorno.value} value={String(giorno.value)}>
                              {giorno.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Dalle ore</Label>
                        <Input
                          type="time"
                          value={chiusuraDalle}
                          onChange={(e) => setChiusuraDalle(e.target.value)}
                          disabled={!canEditGiorniLavorativi || !chiusuraAttiva}
                          data-testid="chiusura-dalle-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Alle ore</Label>
                        <Input
                          type="time"
                          value={chiusuraAlle}
                          onChange={(e) => setChiusuraAlle(e.target.value)}
                          disabled={!canEditGiorniLavorativi || !chiusuraAttiva}
                          data-testid="chiusura-alle-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={canEditGiorniLavorativi ? "" : "hidden"}>
                  <Separator className="my-4" />
                  <Button
                    onClick={handleSaveWorkweek}
                    disabled={savingWorkweek}
                    className={`btn-theme btn-press ${showOnboardingWizard && onboardingStep === 3 ? "animate-pulse ring-2 ring-emerald-400 ring-offset-2 bg-emerald-600 hover:bg-emerald-700" : ""}`}
                    data-testid="save-workweek-btn"
                  >
                    {savingWorkweek ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</>
                    ) : (
                      "Salva Settimana Lavorativa"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Request Limits Settings (Titolare and Amministrativo can view) */}
          <TabsContent value="limits" className={`space-y-6 ${(isTitolare || isAmministrativo) ? "" : "hidden"}`}>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  Limiti Richieste Ferie/Permessi
                  <HelpTooltip content="Imposta fino a quanto tempo in anticipo i dipendenti possono richiedere ferie e permessi. Puoi limitare le richieste al mese corrente, a un numero di mesi specifico, o permettere richieste per tutto l'anno." />
                </CardTitle>
                <CardDescription>
                  {canEditLimitiRichieste 
                    ? "Configura fino a quando i dipendenti possono richiedere ferie e permessi"
                    : "Non hai i permessi per modificare queste impostazioni"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Limite attuale:</span> {getLimiteLabel()}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tipo di limite</Label>
                    <Select
                      value={limiteRichieste}
                      onValueChange={setLimiteRichieste}
                      disabled={!canEditLimitiRichieste}
                    >
                      <SelectTrigger data-testid="limite-richieste-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="questo_mese">Solo questo mese</SelectItem>
                        <SelectItem value="entro_x_mesi">Entro X mesi</SelectItem>
                        <SelectItem value="tutto_anno">Tutto l'anno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className={`space-y-2 ${limiteRichieste !== "entro_x_mesi" ? "hidden" : ""}`}>
                    <Label>Numero di mesi</Label>
                    <Select
                      value={String(limiteMesi)}
                      onValueChange={(v) => setLimiteMesi(parseInt(v))}
                      disabled={!canEditLimitiRichieste}
                    >
                      <SelectTrigger data-testid="limite-mesi-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 9, 12].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n} {n === 1 ? "mese" : "mesi"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-theme-50 rounded-lg p-4 border border-theme-100">
                  <p className="text-sm text-theme-800">
                    <strong>Come funziona:</strong>
                  </p>
                  <ul className="text-sm text-theme-700 mt-2 space-y-1">
                    <li>• <strong>Solo questo mese:</strong> I dipendenti possono richiedere ferie solo per il mese corrente</li>
                    <li>• <strong>Entro X mesi:</strong> I dipendenti possono richiedere ferie entro i prossimi X mesi</li>
                    <li>• <strong>Tutto l'anno:</strong> Nessun limite temporale</li>
                  </ul>
                </div>

                <div className={canEditLimitiRichieste ? "" : "hidden"}>
                  <Separator className="my-4" />
                  <Button
                    onClick={handleSaveLimits}
                    disabled={savingLimits}
                    className="btn-theme btn-press"
                    data-testid="save-limits-btn"
                  >
                    {savingLimits ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</>
                    ) : (
                      "Salva Limiti"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Local Holidays Settings (Titolare and Amministrativo can view) */}
          <TabsContent value="holidays" className={`space-y-6 ${(isTitolare || isAmministrativo) ? "" : "hidden"}`}>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  Festività Locali / Patrono
                  <HelpTooltip content="Aggiungi le festività locali della tua zona, come il giorno del Santo Patrono. Queste date verranno evidenziate nel calendario e considerate come giorni festivi." />
                </CardTitle>
                <CardDescription>
                  Aggiungi festività locali specifiche per la tua zona (es. Santo Patrono)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new holiday - titolare and amministrativo can add */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <Label className="text-sm font-medium">Aggiungi nuova festività</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <Input
                        placeholder="Nome festività (es. San Gennaro)"
                        value={newHoliday.nome}
                        onChange={(e) => setNewHoliday({ ...newHoliday, nome: e.target.value })}
                        data-testid="new-holiday-nome"
                      />
                    </div>
                    <div>
                      <Select
                        value={String(newHoliday.giorno)}
                        onValueChange={(v) => setNewHoliday({ ...newHoliday, giorno: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="new-holiday-giorno">
                          <SelectValue placeholder="Giorno" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((g) => (
                            <SelectItem key={g} value={String(g)}>{g}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Select
                        value={String(newHoliday.mese)}
                        onValueChange={(v) => setNewHoliday({ ...newHoliday, mese: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="new-holiday-mese">
                          <SelectValue placeholder="Mese" />
                        </SelectTrigger>
                        <SelectContent>
                          {MESI.map((m) => (
                            <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    onClick={handleAddHoliday}
                    disabled={addingHoliday}
                    className="btn-theme"
                    data-testid="add-holiday-btn"
                  >
                    {addingHoliday ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Aggiungi Festività
                  </Button>
                </div>

                <Separator />

                {/* List of local holidays */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Festività locali configurate</Label>
                  {localHolidays.length === 0 ? (
                    <p className="text-sm text-slate-500 py-4 text-center">
                      Nessuna festività locale configurata
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {localHolidays.map((holiday) => (
                        <div
                          key={holiday.id}
                          className="flex items-center justify-between p-3 bg-theme-50 rounded-lg border border-theme-100"
                          data-testid={`local-holiday-${holiday.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Flag className="h-4 w-4 text-theme-500" />
                            <div>
                              <p className="font-medium text-slate-900">{holiday.nome}</p>
                              <p className="text-sm text-slate-500">
                                {holiday.giorno} {MESI.find(m => m.value === holiday.mese)?.label}
                              </p>
                            </div>
                          </div>
                          {/* Only titolare can delete holidays */}
                          <div className={user?.ruolo === "titolare" ? "" : "hidden"}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteHolidayId(holiday.id)}
                              className="text-slate-400 hover:text-red-500"
                              data-testid={`delete-holiday-${holiday.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Notifications Settings */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="h-5 w-5 mr-2" />
                  Notifiche Email
                </CardTitle>
                <CardDescription>
                  Scegli quali notifiche vuoi ricevere via email oltre alla campanella
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Email Address Section */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-slate-900">Indirizzo email per le notifiche</p>
                      <p className="text-sm text-slate-500">
                        Vuoi riceverle al tuo indirizzo di registrazione, oppure preferisci un altro indirizzo email?
                      </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <Input
                          type="email"
                          value={notificationEmail}
                          onChange={(e) => setNotificationEmail(e.target.value)}
                          placeholder={registrationEmail}
                          className="h-10"
                          data-testid="notification-email-input"
                        />
                        {notificationEmail !== registrationEmail && notificationEmail && (
                          <p className="text-xs text-slate-500 mt-1">
                            Email di registrazione: {registrationEmail}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={handleChangeNotificationEmail}
                        disabled={changingEmail || notificationEmail === registrationEmail && notificationEmailVerified}
                        className="gap-2"
                        variant={notificationEmailVerified && notificationEmail !== registrationEmail ? "outline" : "default"}
                        data-testid="change-email-btn"
                      >
                        {changingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        {notificationEmail === registrationEmail ? "Usa questo" : "Cambia email"}
                      </Button>
                    </div>
                    
                    {/* Verification Code Input */}
                    {pendingEmailChange && !notificationEmailVerified && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800 mb-2">
                          📧 Abbiamo inviato un codice di verifica a <strong>{notificationEmail}</strong>
                        </p>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="Inserisci codice a 6 cifre"
                            maxLength={6}
                            className="h-10 w-40 text-center font-mono tracking-widest"
                            data-testid="verification-code-input"
                          />
                          <Button
                            onClick={handleVerifyCode}
                            disabled={verifyingCode || verificationCode.length !== 6}
                            className="gap-2"
                            data-testid="verify-code-btn"
                          >
                            {verifyingCode ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                            Verifica
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    {notificationEmail !== registrationEmail && notificationEmail && (
                      notificationEmailVerified ? (
                        <div className="flex items-center gap-2 text-emerald-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm">Email verificata</span>
                        </div>
                      ) : !pendingEmailChange && (
                        <div className="flex items-center gap-2 text-amber-600">
                          <Mail className="h-4 w-4" />
                          <span className="text-sm">Email da verificare</span>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <Separator />
                
                <div className="space-y-4">
                  {/* Titolari notifications */}
                  {(user?.ruolo === "titolare") && (
                    <>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Nuovo dipendente registrato</p>
                          <p className="text-sm text-slate-500">Ricevi un'email quando un nuovo dipendente si registra</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailPreferences.nuovo_dipendente}
                            onChange={(e) => setEmailPreferences({...emailPreferences, nuovo_dipendente: e.target.checked})}
                            className="sr-only peer"
                            data-testid="email-pref-nuovo-dipendente"
                          />
                          <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${emailPreferences.nuovo_dipendente ? 'bg-theme-600 shadow-lg' : 'bg-slate-300'}`}></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Nuova richiesta ferie/permessi</p>
                          <p className="text-sm text-slate-500">Ricevi un'email quando un dipendente invia una richiesta</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailPreferences.nuova_richiesta}
                            onChange={(e) => setEmailPreferences({...emailPreferences, nuova_richiesta: e.target.checked})}
                            className="sr-only peer"
                            data-testid="email-pref-nuova-richiesta"
                          />
                          <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${emailPreferences.nuova_richiesta ? 'bg-theme-600 shadow-lg' : 'bg-slate-300'}`}></div>
                        </label>
                      </div>
                    </>
                  )}
                  
                  {/* Employee/Amministrativo notifications */}
                  {(user?.ruolo === "dipendente" || user?.ruolo === "amministrativo") && (
                    <>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Richiesta approvata</p>
                          <p className="text-sm text-slate-500">Ricevi un'email quando la tua richiesta viene approvata</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailPreferences.richiesta_approvata}
                            onChange={(e) => setEmailPreferences({...emailPreferences, richiesta_approvata: e.target.checked})}
                            className="sr-only peer"
                            data-testid="email-pref-richiesta-approvata"
                          />
                          <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${emailPreferences.richiesta_approvata ? 'bg-theme-600 shadow-lg' : 'bg-slate-300'}`}></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Richiesta rifiutata</p>
                          <p className="text-sm text-slate-500">Ricevi un'email quando la tua richiesta viene rifiutata</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailPreferences.richiesta_rifiutata}
                            onChange={(e) => setEmailPreferences({...emailPreferences, richiesta_rifiutata: e.target.checked})}
                            className="sr-only peer"
                            data-testid="email-pref-richiesta-rifiutata"
                          />
                          <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${emailPreferences.richiesta_rifiutata ? 'bg-theme-600 shadow-lg' : 'bg-slate-300'}`}></div>
                        </label>
                      </div>
                    </>
                  )}
                  
                  {/* All users */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Evento assegnato</p>
                      <p className="text-sm text-slate-500">Ricevi un'email quando viene assegnato un evento (straordinario/malattia)</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailPreferences.evento_assegnato}
                        onChange={(e) => setEmailPreferences({...emailPreferences, evento_assegnato: e.target.checked})}
                        className="sr-only peer"
                        data-testid="email-pref-evento-assegnato"
                      />
                      <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${emailPreferences.evento_assegnato ? 'bg-theme-600 shadow-lg' : 'bg-slate-300'}`}></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Nuovo evento in calendario</p>
                      <p className="text-sm text-slate-500">Ricevi un'email quando viene aggiunto un evento generale al calendario</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailPreferences.nuovo_evento_calendario}
                        onChange={(e) => setEmailPreferences({...emailPreferences, nuovo_evento_calendario: e.target.checked})}
                        className="sr-only peer"
                        data-testid="email-pref-nuovo-evento"
                      />
                      <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${emailPreferences.nuovo_evento_calendario ? 'bg-theme-600 shadow-lg' : 'bg-slate-300'}`}></div>
                    </label>
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <Button
                  onClick={handleSaveEmailPreferences}
                  disabled={savingEmailPrefs}
                  className="btn-theme btn-press"
                  data-testid="save-email-prefs-btn"
                >
                  {savingEmailPrefs ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</>
                  ) : (
                    "Salva Preferenze"
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visibility Settings - Only for Titolare */}
          <TabsContent value="visibility" className={`space-y-6 ${user?.ruolo === "titolare" ? "" : "hidden"}`}>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-theme-600" />
                  Visibilità
                </CardTitle>
                <CardDescription>
                  Gestisci cosa viene visualizzato nella tua dashboard e in quella dei dipendenti
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Prospetto Ferie Titolare */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Mostra Prospetto Ferie nella Home</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {mostraProspettoFerie 
                        ? "Il prospetto ferie dipendenti è visibile nella tua dashboard Home" 
                        : "Il prospetto ferie dipendenti è nascosto dalla tua dashboard Home"}
                    </p>
                  </div>
                  <Switch
                    checked={mostraProspettoFerie}
                    onCheckedChange={setMostraProspettoFerie}
                    data-testid="mostra-prospetto-toggle"
                  />
                </div>
                
                {/* Saldi Dipendenti */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Mostra ferie residue ai dipendenti</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {mostraSaldiDipendenti 
                        ? "I dipendenti possono vedere i giorni di ferie residue nella loro dashboard" 
                        : "I dipendenti possono solo inviare richieste di ferie e permessi (senza vedere le ferie residue)"}
                    </p>
                  </div>
                  <Switch
                    checked={mostraSaldiDipendenti}
                    onCheckedChange={setMostraSaldiDipendenti}
                    data-testid="mostra-saldi-toggle"
                  />
                </div>
                
                <Button
                  onClick={handleSaveGeneral}
                  disabled={savingGeneral}
                  className="btn-theme btn-press"
                  data-testid="save-visibility-btn"
                >
                  {savingGeneral ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</>
                  ) : (
                    "Salva Modifiche"
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete Holiday Confirmation */}
        <AlertDialog open={!!deleteHolidayId} onOpenChange={() => setDeleteHolidayId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler eliminare questa festività locale?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDeleteHoliday}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-holiday-btn"
              >
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
